import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/shadcn/form'
import { FileInput, FileUploader, FileUploaderContent, FileUploaderItem } from '@/components/shadcn/file-uploader'
import { IconArrowDownOutline } from '@icons/IconArrowDownOutline'
import { IconFileOutline } from '@icons/IconFileOutline'
import { IconUploadOutline } from '@icons/IconUploadOutline'
import clsx from 'clsx'
import { useState } from 'react'
import { type Control, FieldPath, FieldValues } from 'react-hook-form'

interface FileUploadFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> {
  control: Control<TFieldValues>
  name: TName
  label: string
  maxSize?: number
  maxFiles?: number
  multiple?: boolean
  acceptedFormats?: string[]
  placeholder?: string
  showFileInfo?: boolean
  className?: string
  disabled?: boolean
  onFileChange?: (file: File | null | File[]) => void
}

const DEFAULT_ACCEPTED_FORMATS = [
  '.pdf',
  '.xlsx',
  '.xls',
  '.csv',
  '.doc',
  '.docx',
  '.txt',
  '.jpg',
  '.jpeg',
  '.png',
  '.json',
]

function getAcceptedFiles(value: unknown, multiple: boolean): File[] {
  if (multiple) {
    if (Array.isArray(value)) {
      return value.filter((file): file is File => file instanceof File)
    }
    return value instanceof File ? [value] : []
  }

  if (value instanceof File) return [value]
  if (Array.isArray(value) && value[0] instanceof File) return [value[0]]
  return []
}

function formatDisplayExtensions(extensions: string[]): string {
  return extensions.map((format) => format.replace('.', '').toUpperCase()).join(', ')
}

function createAcceptedFileTypes(extensions: string[]): Record<string, string[]> {
  const acceptedFileTypes: Record<string, string[]> = {}

  const mimeByExtension: Record<string, string> = {
    '.pdf': 'application/pdf',
    '.csv': 'text/csv',
    '.xls': 'application/vnd.ms-excel',
    '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    '.doc': 'application/msword',
    '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    '.txt': 'text/plain',
    '.jpg': 'image/jpeg',
    '.jpeg': 'image/jpeg',
    '.png': 'image/png',
    '.gif': 'image/gif',
    '.webp': 'image/webp',
    '.json': 'application/json',
  }

  extensions.forEach((extension) => {
    const normalizedExtension = extension.startsWith('.') ? extension.toLowerCase() : `.${extension.toLowerCase()}`
    const mimeType = mimeByExtension[normalizedExtension] ?? 'application/octet-stream'

    if (!acceptedFileTypes[mimeType]) {
      acceptedFileTypes[mimeType] = []
    }

    acceptedFileTypes[mimeType].push(normalizedExtension)
  })

  return acceptedFileTypes
}

export function FileUploadField<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  control,
  name,
  label,
  maxSize = 10 * 1024 * 1024,
  maxFiles = 1,
  multiple = false,
  acceptedFormats = DEFAULT_ACCEPTED_FORMATS,
  placeholder,
  showFileInfo = true,
  className,
  disabled = false,
  onFileChange,
}: FileUploadFieldProps<TFieldValues, TName>) {
  const [isDragging, setIsDragging] = useState(false)

  const resolvedMaxFiles = multiple ? Math.max(1, maxFiles) : 1
  const maxSizeMB = maxSize / 1024 / 1024
  const acceptedLabel = formatDisplayExtensions(acceptedFormats)
  const acceptedFileTypes = createAcceptedFileTypes(acceptedFormats)

  return (
    <FormField
      control={control}
      name={name}
      render={({ field: { value, onChange } }) => {
        const files = getAcceptedFiles(value, multiple)

        const handleFileSelect = (newFiles: File[] | null) => {
          const selectedFiles = newFiles ?? []

          if (multiple) {
            const nextFiles = selectedFiles.slice(0, resolvedMaxFiles)
            onChange(nextFiles)
            onFileChange?.(nextFiles)
            return
          }

          const nextFile = selectedFiles[0] ?? null
          onChange(nextFile)
          onFileChange?.(nextFile)
        }

        return (
          <FormItem className={className}>
            <FormLabel>{label}</FormLabel>
            <FormControl>
              <FileUploader
                value={files}
                onValueChange={handleFileSelect}
                dropzoneOptions={{
                  accept: acceptedFormats.length ? acceptedFileTypes : undefined,
                  maxFiles: resolvedMaxFiles,
                  maxSize,
                  multiple,
                  disabled,
                  onDragEnter: () => setIsDragging(true),
                  onDragLeave: () => setIsDragging(false),
                  onDrop: () => setIsDragging(false),
                }}
                className="relative space-y-1"
              >
                <FileInput
                  className={clsx('border-2 border-dashed border-gray-300 bg-background transition-colors', {
                    'border-solid border-primary bg-primary/5': isDragging,
                  })}
                >
                  <div className="flex w-full flex-col items-center justify-center gap-y-3 pb-8 pt-6">
                    <IconUploadOutline className={clsx('size-10 text-gray-400', { hidden: isDragging })} />
                    <IconArrowDownOutline className={clsx('size-10 text-primary', { hidden: !isDragging })} />
                    <div className="text-center">
                      <p className="text-sm font-medium text-gray-700">
                        {isDragging
                          ? 'Drop file here'
                          : placeholder || `Click to select or drag and drop${multiple ? ' files' : ''}`}
                      </p>
                      <p className="mt-1 text-xs text-gray-500">
                        {acceptedLabel} up to {maxSizeMB}MB{multiple ? ` (${resolvedMaxFiles} files max)` : ''}
                      </p>
                    </div>
                  </div>
                </FileInput>

                {files.length > 0 && (
                  <FileUploaderContent>
                    {files.map((file, index) => (
                      <FileUploaderItem key={`${file.name}-${index}`} index={index}>
                        <div className="flex items-center gap-x-2">
                          <IconFileOutline className="size-4" />
                          <span className="max-w-md truncate text-sm">{file.name}</span>
                          {showFileInfo && (
                            <span className="text-xs text-gray-500">({(file.size / 1024 / 1024).toFixed(2)} MB)</span>
                          )}
                        </div>
                      </FileUploaderItem>
                    ))}
                  </FileUploaderContent>
                )}
              </FileUploader>
            </FormControl>
            <FormMessage />
          </FormItem>
        )
      }}
    />
  )
}
